<script>
	import { faTrash } from "@fortawesome/free-solid-svg-icons";
	import { doc, getFirestore, setDoc } from "firebase/firestore";
	import { v4 as uuid } from "uuid";

	import SvelteFa from "$lib/components/SvelteFa.svelte";
	import { store } from "$lib/utilities/store";

	import RecordFields from "./RecordFields.svelte";

	export let col;
	export let docId;
	export let editMode;
	export let tableFields = [];
	export let layoutClass;

	$: groupArray =
		$store.userProfile?.collectionCriteria?.[col]?.formLayout || [];

	$: availableFieldsByGroup = groupArray.reduce((acc, group) => {
		const allUsedFields = [
			...new Set(groupArray.flatMap((g) => g.fieldOrder)),
		];
		acc[group.id] = tableFields.filter(
			({ heading }) =>
				!allUsedFields.map((f) => f.fieldName).includes(heading),
		);
		return acc;
	}, {});

	let currentGroupOrder = [];
	let groupDragIndex = null;
	let isPersistingGroupOrder = false;

	$: {
		const sourceGroupIds = Array.isArray(groupArray)
			? groupArray.map((group) => group.id)
			: [];

		if (!sourceGroupIds.length) {
			if (currentGroupOrder.length) currentGroupOrder = [];
		} else {
			const missingIds = sourceGroupIds.filter(
				(id) => !currentGroupOrder.includes(id),
			);
			const filteredCurrentOrder = currentGroupOrder.filter((id) =>
				sourceGroupIds.includes(id),
			);

			if (
				missingIds.length ||
				filteredCurrentOrder.length !== currentGroupOrder.length
			) {
				currentGroupOrder = [...filteredCurrentOrder, ...missingIds];
			} else if (
				!isPersistingGroupOrder &&
				(sourceGroupIds.length !== currentGroupOrder.length ||
					sourceGroupIds.some(
						(id, index) => id !== currentGroupOrder[index],
					))
			) {
				currentGroupOrder = [...sourceGroupIds];
			} else if (!currentGroupOrder.length) {
				currentGroupOrder = [...sourceGroupIds];
			}
		}
	}

	$: orderedGroups =
		currentGroupOrder.length && Array.isArray(groupArray)
			? currentGroupOrder
					.map((groupId) =>
						groupArray.find((group) => group.id === groupId),
					)
					.filter(Boolean)
			: Array.isArray(groupArray)
				? groupArray
				: [];

	const addGroup = async () => {
		const newGroup = {
			id: uuid(),
			label: "New Group",
			fieldOrder: [],
			groupClass: "",
		};

		const formLayout =
			$store.userProfile?.collectionCriteria?.[col]?.formLayout || [];
		formLayout.push(newGroup);

		const mergedCriteria = {
			...($store.userProfile?.collectionCriteria?.[col] || {}),
			formLayout,
		};
		const updates = { collectionCriteria: { [col]: mergedCriteria } };

		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);

		await setDoc(profileDocRef, updates, { merge: true });
	};

	const deleteGroup = async (groupId) => {
		const formLayout = (
			$store.userProfile?.collectionCriteria?.[col]?.formLayout || []
		).filter((group) => group.id !== groupId);

		const mergedCriteria = {
			...($store.userProfile?.collectionCriteria?.[col] || {}),
			formLayout,
		};
		const updates = { collectionCriteria: { [col]: mergedCriteria } };

		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);

		await setDoc(profileDocRef, updates, { merge: true });
	};

	const addFieldToGroup = async (groupId, fieldName) => {
		const formLayout = (
			$store.userProfile?.collectionCriteria?.[col]?.formLayout || []
		).map((group) =>
			group.id === groupId
				? { ...group, fieldOrder: [...group.fieldOrder, { fieldName }] }
				: group,
		);

		const updates = {
			collectionCriteria: {
				[col]: {
					...$store.userProfile?.collectionCriteria?.[col],
					formLayout,
				},
			},
		};

		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);

		await setDoc(profileDocRef, updates, { merge: true });
	};

	const getAvailableFields = (groupId) =>
		availableFieldsByGroup[groupId] || [];

	const updateGroup = async (groupId, newValue, field) => {
		const existingGroups =
			$store.userProfile?.collectionCriteria?.[col]?.formLayout || [];
		const targetGroup = existingGroups.find(
			(group) => group.id === groupId,
		);

		if (!targetGroup || targetGroup[field] === newValue) return;

		const formLayout = existingGroups.map((group) =>
			group.id === groupId ? { ...group, [field]: newValue } : group,
		);

		const mergedCriteria = {
			...($store.userProfile?.collectionCriteria?.[col] || {}),
			formLayout,
		};

		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);

		const updates = { collectionCriteria: { [col]: mergedCriteria } };

		await setDoc(profileDocRef, updates, { merge: true });
	};

	const persistGroupOrder = async (orderedGroupIds = []) => {
		const existingGroups =
			$store.userProfile?.collectionCriteria?.[col]?.formLayout || [];

		if (!existingGroups.length || !orderedGroupIds.length) return;

		const groupsById = existingGroups.reduce((acc, existingGroup) => {
			acc[existingGroup.id] = existingGroup;
			return acc;
		}, {});

		const orderedLayout = orderedGroupIds
			.map((groupId) => groupsById[groupId])
			.filter(Boolean);

		const remainingGroups = existingGroups.filter(
			(existingGroup) => !orderedGroupIds.includes(existingGroup.id),
		);

		const formLayout = [...orderedLayout, ...remainingGroups];

		const mergedCriteria = {
			...($store.userProfile?.collectionCriteria?.[col] || {}),
			formLayout,
		};

		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);

		const updates = { collectionCriteria: { [col]: mergedCriteria } };

		isPersistingGroupOrder = true;
		try {
			await setDoc(profileDocRef, updates, { merge: true });
		} finally {
			isPersistingGroupOrder = false;
		}
	};

	const reorderGroup = async (targetIndex) => {
		if (
			groupDragIndex === null ||
			targetIndex === groupDragIndex ||
			targetIndex < 0 ||
			targetIndex > currentGroupOrder.length
		)
			return;

		const updatedOrder = [...currentGroupOrder];
		const [movedGroupId] = updatedOrder.splice(groupDragIndex, 1);
		updatedOrder.splice(targetIndex, 0, movedGroupId);

		groupDragIndex = null;
		currentGroupOrder = updatedOrder;
		await persistGroupOrder(updatedOrder);
	};

	const handleGroupDragStart = (event, index) => {
		if (!editMode) return;
		groupDragIndex = index;
		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = "move";
			event.dataTransfer.setData("text/plain", currentGroupOrder[index]);
		}
	};

	const handleGroupDrop = async (event, index) => {
		if (!editMode) return;
		event.preventDefault();
		event.stopPropagation();
		await reorderGroup(index);
	};

	const handleGroupDragOver = (event) => {
		if (!editMode || groupDragIndex === null) return;
		event.preventDefault();
	};

	const handleGroupDragEnd = () => {
		groupDragIndex = null;
	};

	const handleGroupsContainerDragOver = (event) => {
		if (!editMode || groupDragIndex === null) return;
		event.preventDefault();
	};

	const handleGroupsContainerDrop = async (event) => {
		if (!editMode) return;
		event.preventDefault();
		await reorderGroup(currentGroupOrder.length);
	};
</script>

<div
	role={editMode ? "list" : undefined}
	class={layoutClass}
	on:dragover={handleGroupsContainerDragOver}
	on:drop={handleGroupsContainerDrop}
>
	{#each orderedGroups as group, index (group.id)}
		<div
			class={`${group.groupClass} ${editMode ? "hover:cursor-pointer" : ""}`}
			draggable={editMode}
			role={editMode ? "listitem" : undefined}
			on:dragstart={(event) => handleGroupDragStart(event, index)}
			on:dragover={handleGroupDragOver}
			on:drop={(event) => handleGroupDrop(event, index)}
			on:dragend={handleGroupDragEnd}
		>
			<div class="flex gap-2 justify-between mb-2 sm:mb-4">
				<div class="w-full">
					{#if editMode}
						<input
							type="text"
							class="input input-bordered input-primary input-sm w-full"
							value={group.label}
							on:change={(event) =>
								updateGroup(
									group.id,
									event.currentTarget.value,
									"label",
								)}
						/>
					{:else}
						<h3
							class="text-lg! font-semibold tracking-wide uppercase"
						>
							{group.label}
						</h3>
					{/if}
				</div>
				{#if editMode}
					<div class="flex gap-2">
						<select
							class="select select-bordered select-primary select-sm"
							on:change={(event) => {
								addFieldToGroup(
									group.id,
									event.currentTarget.value,
								);
								event.currentTarget.value = "";
							}}
							disabled={!getAvailableFields(group.id).length}
						>
							<option value="">
								{getAvailableFields(group.id).length
									? "Add field..."
									: "No fields available"}
							</option>
							{#each getAvailableFields(group.id) as fieldOption}
								<option value={fieldOption.heading}>
									{fieldOption.heading}
								</option>
							{/each}
						</select>
						<input
							type="text"
							class="input input-bordered input-primary input-sm"
							placeholder="Grid classes"
							value={group.groupClass ?? ""}
							on:change={(event) =>
								updateGroup(
									group.id,
									event.currentTarget.value,
									"groupClass",
								)}
						/>
						<input
							type="text"
							class="input input-bordered input-primary input-sm"
							placeholder="Field container classes"
							value={group.fieldContainerClass ?? ""}
							on:change={(event) =>
								updateGroup(
									group.id,
									event.currentTarget.value,
									"fieldContainerClass",
								)}
						/>
						<button
							class="btn btn-sm btn-ghost text-error"
							type="button"
							on:click={() => deleteGroup(group.id)}
							title="Delete group"
						>
							<SvelteFa icon={faTrash} />
						</button>
					</div>
				{/if}
			</div>

			{#if Array.isArray(group.fieldOrder) && group.fieldOrder.length}
				<RecordFields {col} {docId} {editMode} {group} {tableFields} />
			{:else}
				<p class="text-center text-sm text-base-content/70">
					No form fields selected for this group.
					{#if editMode}
						<span class="block"
							>Use the dropdown above to add fields.</span
						>
					{/if}
				</p>
			{/if}
		</div>
	{/each}
</div>

{#if editMode}
	<button class="btn btn-primary" type="button" on:click={addGroup}>
		Add Group
	</button>
{/if}
